import React, { useEffect, useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import MessagingList from "./messagingList";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import { useDispatch, useSelector } from "react-redux";

import {
  getCurrentUser,
  getUserProfilePic,
  getUserProfilePicTwo,
  loadUserProfilePicTwo,
} from "../../app/store/auth";

import {
  getComponentUserStatus,
  UpdateComponentsUserStatus,
} from "../../app/store/tools";
import { uploadFileToBlob } from "../../services/blobUploadService";
import {
  addSupportAppMessage, getSupportAddedNewMsgStatus, getSupportAppMsgList, getSupportAppMsgReadStatus,
  getSupportMsgPopupDocSasUrls, loadSupportAppMsgList, supportAppMsgReadStatusUpdated, supportAppNewMsgAdded,
  updateSupportMessageRead
} from "../../app/store/support";

const MessagingPopup = ({
  msgShowHide,
  handleMsgModalShowHide,
  message,
  handleSendMessage,
  handleSupportMessage,
}) => {
  const dispatch = useDispatch();
  const currentUser = getCurrentUser();

  const [messageList, setMessageList] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [content, setContent] = useState("");
  const [profilePicData, setProfilePicData] = useState(null);
  const [profilePicTwoData, setProfilePicTwoData] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef(null);

  // Redux Selectors
  const componentUserStatusSelector = useSelector(getComponentUserStatus);
  const userProfilePicSelectorTwo = useSelector(getUserProfilePicTwo);
  const userProfilePicSelector = useSelector(getUserProfilePic);

  const appMsgListSelector = useSelector(getSupportAppMsgList);
  const supportAddedNewMsgStatusSelector = useSelector(getSupportAddedNewMsgStatus);
  const appMsgReadStatusSelector = useSelector(getSupportAppMsgReadStatus);
  const supportMsgPopupDocSasUrlsSelector = useSelector(getSupportMsgPopupDocSasUrls);

  const formatFileSize = (sizeInBytes) => {
    if (!sizeInBytes && sizeInBytes !== 0) return "";
    if (sizeInBytes < 1024) return `${sizeInBytes} B`;
    if (sizeInBytes < 1024 * 1024) return `${(sizeInBytes / 1024).toFixed(1)} KB`;
    return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const toolbar = [
    [
      "undo",
      "redo",
      "font",
      "fontSize",
      "formatBlock",
      "bold",
      "underline",
      "italic",
      "fontColor",
      "hiliteColor",
      "removeFormat",
      "link",
      "image",
    ],
  ];

  // ------------------------------------------------------------
  // Load message thread when modal opens or message.id changes
  // ------------------------------------------------------------
  useEffect(() => {
    if (!message?.id) return;
    dispatch(loadSupportAppMsgList(message.id));
    setDocuments([]);
    setContent("");
    setMessageList([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [message?.id, dispatch]);

  // ------------------------------------------------------------
  // Build message list from Redux store
  // ------------------------------------------------------------
  useEffect(() => {
    if (!appMsgListSelector || appMsgListSelector?.length === 0) return;

    const msgList = appMsgListSelector.map((item) => {
      const isSender = item.fromId === parseInt(currentUser.id);
      const performer = `${item.fromName} ${item.fromLastName}`;

      if (!isSender) {
        dispatch(loadUserProfilePicTwo(item.fromId));
      }

      return {
        author: isSender ? "send" : "receive",
        data: item.message,
        performer,
        date: item.messageDate,
        documents: item.documents,
        id: item.id,
        readStatus: item.readStatus,
      };
    });

    setMessageList(msgList);

    // Mark unread threads as read
    const unread = msgList.some((x) => x.readStatus !== true);

    if (unread && message?.id) {
      dispatch(updateSupportMessageRead(message.id, currentUser.id));
    }

  }, [appMsgListSelector]);

  // ------------------------------------------------------------
  // Load current user's picture
  // ------------------------------------------------------------
  useEffect(() => {
    if (userProfilePicSelector) {
      setProfilePicData("data:image/jpeg;base64," + userProfilePicSelector);
    }
  }, [userProfilePicSelector]);

  // ------------------------------------------------------------
  // Load other party's picture
  // ------------------------------------------------------------
  useEffect(() => {
    if (userProfilePicSelectorTwo) {
      setProfilePicTwoData("data:image/jpeg;base64," + userProfilePicSelectorTwo);
    }
  }, [userProfilePicSelectorTwo]);

  // ------------------------------------------------------------
  // Handle read-status acknowledgement
  // ------------------------------------------------------------
  useEffect(() => {
    if (!appMsgReadStatusSelector) return;

    const msgItems = messageList.map((m) => m.id).join(",");
    if (!msgItems) return;

    handleUpdateComponent(msgItems);
    supportAppMsgReadStatusUpdated(false);

    if (handleSendMessage) handleSendMessage();
  }, [appMsgReadStatusSelector, messageList]);

  // ------------------------------------------------------------
  // Update component user status tracking
  // ------------------------------------------------------------
  const handleUpdateComponent = (compItems) => {
    const existing = componentUserStatusSelector.find((x) => x.compId === 9);
    const previousItems = existing?.compItems || "";

    const compObj = {
      compName: "Messages",
      performerId: currentUser.id,
      compItems,
      ciid: message.ciid,
      wiid: message.wiid,
      iiid: message.iiid,
      compId: 9,
    };

    if (compItems !== "" && compItems !== previousItems) {
      dispatch(UpdateComponentsUserStatus(compObj));
    }
  };

  // ------------------------------------------------------------
  // Editor Change
  // ------------------------------------------------------------
  const handleChange = (newContent) => setContent(newContent);

  // ------------------------------------------------------------
  // File Upload
  // ------------------------------------------------------------
  const onFileChange = (event) => {
    const newFiles = Array.from(event.target.files).map((file) => ({
      name: file.name,
      type: file.type,
      size: file.size,
      file,
    }));

    setDocuments((prev) => [...prev, ...newFiles]);
    // Allow selecting the same file again on the next click.
    event.target.value = "";
  };

  // ------------------------------------------------------------
  // Remove File
  // ------------------------------------------------------------
  const handleFileRemove = (item) => {
    setDocuments((prev) => prev.filter((doc) => doc !== item));
  };

  // ------------------------------------------------------------
  // Submit New Message
  // ------------------------------------------------------------
  const onSubmit = () => {
    const data = new FormData();
    data.append("message", content);
    data.append("performerId", currentUser.id);
    data.append("communication", JSON.stringify(message));
    data.append("upDocs", JSON.stringify(documents));
    data.append("files", JSON.stringify(documents.map((file) => {
      return {
        name: file.name,
        type: file.type,
        size: file.size
      }
    })));

    dispatch(addSupportAppMessage(data));
  };

  // ------------------------------------------------------------
  // After new message successfully added
  // ------------------------------------------------------------
  useEffect(() => {
    if (!supportAddedNewMsgStatusSelector?.state) return;

    dispatch(loadSupportAppMsgList(message.id));

    if (handleSupportMessage) {
      handleSupportMessage(content, supportAddedNewMsgStatusSelector.obj);
    }

    setDocuments([]);
    setContent("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [supportAddedNewMsgStatusSelector]);

  useEffect(() => {
    const uploadAllDocuments = async () => {
      if (supportMsgPopupDocSasUrlsSelector?.length > 0) {
        try {
          await Promise.all(
            documents.map((file, key) =>
              uploadFileToBlob(
                file.file,
                supportMsgPopupDocSasUrlsSelector[key]?.uploadUrl,
                (progress) => {
                  console.log(`Upload progress: ${progress}%`);
                }
              )
            )
          );

          // Runs only after ALL uploads finish   
          console.log("All files uploaded successfully!");
          dispatch(supportAppNewMsgAdded({ sasUrls: null, msg: null }));
          setDocuments([]);
        } catch (error) {
          console.error("Upload failed:", error);
        }
      }
    };

    uploadAllDocuments();
  }, [supportMsgPopupDocSasUrlsSelector]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).map((file) => ({
      name: file.name,
      type: file.type,
      size: file.size,
      file,
    }));
    setDocuments((prev) => [...prev, ...files]);
  };

  return (
    <Modal show={msgShowHide} size="xl" centered className="modern-modal">
      <style>{`
        :root {
          --primary-color: #2563eb;
          --primary-hover: #1d4ed8;
          --primary-soft: #eff6ff;
          --secondary-color: #64748b;
          --secondary-hover: #475569;
          --border-color: #e2e8f0;
          --bg-light: #f8fafc;
          --text-main: #1e293b;
          --text-muted: #64748b;
          --radius-md: 12px;
          --radius-sm: 8px;
          --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
          --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1);
        }

        .modern-modal .modal-content {
          border: none;
          border-radius: var(--radius-md);
          box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1);
          overflow: hidden;
        }

        .modern-modal .modal-header {
          background: #fff;
          border-bottom: 1px solid var(--border-color);
          padding: 18px 24px;
        }

        .modern-modal .modal-title {
          font-size: 1.1rem;
          font-weight: 700;
          color: var(--text-main);
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .modern-modal .modal-body {
          padding: 0;
          background: #fff;
        }

        /* ── Modern Chat Thread Container ── */
        .messages-thread-container {
          background: var(--bg-light);
          padding: 10px 24px;
          height: 100%;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 transparent;
          display: flex;
          flex-direction: column-reverse;
          gap: 24px;
        }
        .messages-thread-container::-webkit-scrollbar {
          width: 6px;
        }
        .messages-thread-container::-webkit-scrollbar-track {
          background: transparent;
        }
        .messages-thread-container::-webkit-scrollbar-thumb {
          background-color: #cbd5e1;
          border-radius: 99px;
        }

        /* ── Modern Chat Message Item ── */
        .wf-chat-item {
          display: flex;
          gap: 14px;
          max-width: 90%;
          align-items: flex-end;
          animation: rtMsgTabFadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) both;
          margin-top: 24px;
        }
        .wf-chat-item.is-sent {
          margin-left: auto;
          flex-direction: row-reverse;
        }
        .wf-chat-item.is-received {
          margin-right: auto;
        }

        /* Avatar styling */
        .wf-chat-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 700;
          flex-shrink: 0;
          box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
          border: 2px solid #ffffff;
          background: var(--bg-light);
        }
        .wf-chat-item.is-sent .wf-chat-avatar {
          background: linear-gradient(135deg, var(--primary-color), var(--primary-hover));
          color: #ffffff;
        }
        .wf-chat-item.is-received .wf-chat-avatar {
          color: #475569;
        }

        /* Bubble styling */
        .wf-chat-bubble {
          display: flex;
          flex-direction: column;
          gap: 6px;
          min-width: 140px;
        }
        .wf-chat-bubble-content {
          padding: 14px 18px;
          font-size: 13.5px;
          line-height: 1.6;
          word-break: break-word;
          box-shadow: 0 2px 4px rgb(0 0 0 / 0.02);
        }
        .wf-chat-item.is-sent .wf-chat-bubble-content {
          background: var(--primary-soft);
          border-radius: 20px 20px 4px 20px;
          border: 1px solid rgba(37, 99, 235, 0.15);
        }
        .wf-chat-item.is-sent .wf-chat-bubble-content,
        .wf-chat-item.is-sent .wf-chat-bubble-content *:not(a) {
          color: #1e293b;
        }
        .wf-chat-item.is-sent .wf-chat-bubble-content a {
          color: var(--primary-color);
          text-decoration: underline;
          font-weight: 500;
        }
        .wf-chat-item.is-received .wf-chat-bubble-content {
          background: #f1f5f9;
          border-radius: 20px 20px 20px 4px;
          border: 1px solid #e2e8f0;
        }
        .wf-chat-item.is-received .wf-chat-bubble-content,
        .wf-chat-item.is-received .wf-chat-bubble-content *:not(a) {
          color: #1e293b;
        }
        .wf-chat-item.is-received .wf-chat-bubble-content a {
          color: var(--primary-color);
          text-decoration: underline;
          font-weight: 500;
        }

        /* Meta / Sender Name / Time styling */
        .wf-chat-meta {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 11.5px;
          color: #94a3b8;
          margin-bottom: 2px;
          padding: 0 4px;
        }
        .wf-chat-item.is-sent .wf-chat-meta {
          justify-content: flex-end;
        }
        .wf-chat-sender {
          font-weight: 600;
          color: #64748b;
        }
        .wf-chat-time {
          font-weight: 500;
        }

        /* Attachment styling */
        .wf-chat-attachments {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-top: 10px;
          padding: 0;
          list-style: none;
        }
        .wf-attachment-item {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 14px;
          border-radius: 12px;
          font-size: 12.5px;
          font-weight: 500;
          transition: all 0.2s ease;
          width: 100%;
          box-sizing: border-box;
        }
        .wf-chat-item.is-sent .wf-attachment-item {
          background: #ffffff;
          color: #475569;
          border: 1px solid rgba(37, 99, 235, 0.2);
          box-shadow: 0 1px 2px rgb(0 0 0 / 0.02);
        }
        .wf-chat-item.is-sent .wf-attachment-item:hover {
          border-color: var(--primary-color);
          color: #0f172a;
        }
        .wf-chat-item.is-received .wf-attachment-item {
          background: #ffffff;
          color: #475569;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 2px rgb(0 0 0 / 0.02);
        }
        .wf-chat-item.is-received .wf-attachment-item:hover {
          border-color: #cbd5e1;
          color: #0f172a;
        }
        .wf-attachment-item button {
          background: none;
          border: none;
          padding: 0;
          color: inherit;
          font-weight: inherit;
          font-size: inherit;
          text-align: left;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          cursor: pointer;
          outline: none;
        }
        .wf-attachment-item button i {
          font-size: 14px;
          opacity: 0.8;
        }

        /* ── Editor Section Styling ── */
        .wf-reply-panel {
          background: #ffffff;
          border-left: 1px solid #f1f5f9;
        }

        /* ── Drop Zone Styling ── */
        .drop-zone {
          border: 1.5px dashed #cbd5e1;
          border-radius: 16px;
          background: #ffffff;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 110px;
          cursor: pointer;
          margin-bottom: 24px;
          padding: 20px;
        }

        .drop-zone:hover, .drop-zone.dragging {
          border-color: var(--primary-color);
          background: var(--primary-soft);
          transform: translateY(-2px);
        }

        .drop-zone i {
          color: var(--primary-color);
          font-size: 1.8rem;
          margin-bottom: 8px;
          opacity: 0.8;
        }

        .drop-zone p {
          font-size: 14px;
          color: var(--text-main);
          font-weight: 600;
          margin: 0;
        }

        .drop-zone span {
          font-size: 12.5px;
          color: var(--text-muted);
          margin-top: 2px;
        }

        /* ── File Preview Grid ── */
        .file-preview-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 12px;
          margin-bottom: 20px;
        }

        .file-card {
          background: #fff;
          border: 1px solid var(--border-color);
          border-radius: var(--radius-sm);
          padding: 10px 12px;
          display: flex;
          align-items: center;
          gap: 10px;
          box-shadow: var(--shadow-sm);
          transition: all 0.2s;
        }

        .file-icon-wrapper {
          width: 32px;
          height: 32px;
          background: var(--primary-soft);
          color: var(--primary-color);
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
          font-size: 14px;
          flex-shrink: 0;
        }

        .file-content {
          flex: 1;
          min-width: 0;
        }

        .file-title {
          font-size: 12px;
          font-weight: 600;
          color: var(--text-main);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .file-meta {
          font-size: 10px;
          color: var(--text-muted);
        }

        .btn-remove-file {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          border: none;
          background: transparent;
          color: var(--text-muted);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-remove-file:hover {
          background: #fee2e2;
          color: #dc2626;
        }

        /* ── Action Buttons ── */
        .wf-btn-group {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-top: 24px;
        }

        .wf-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px 20px;
          font-size: 13px;
          font-weight: 600;
          border-radius: var(--radius-sm);
          cursor: pointer;
          transition: all 0.2s;
          border: 1px solid transparent;
        }

        .wf-btn-primary {
          background: var(--primary-color);
          color: #ffffff;
        }

        .wf-btn-primary:hover {
          background: var(--primary-hover);
          transform: translateY(-1px);
        }

        .wf-btn-secondary {
          background: #fff;
          color: var(--secondary-color);
          border-color: var(--border-color);
        }

        .wf-btn-secondary:hover {
          background: var(--bg-light);
          border-color: var(--secondary-hover);
        }
      `}</style>

      <Modal.Header closeButton onHide={() => handleMsgModalShowHide("")}>
        <Modal.Title>
          <i className="fa fa-comments" aria-hidden="true"></i>
          Messages
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0">
        <div className="row m-0" style={{ minHeight: "500px", height: "75vh" }}>
          {/* LEFT COLUMN: MESSAGE LIST (60%) */}
          <div className="col-md-7 p-0 d-flex flex-column bg-light" style={{ height: "100%", overflow: "hidden" }}>
            <div className="messages-thread-container flex-grow-1" style={{ marginBottom: 0 }}>
              <div className="bg-transparent">
                {messageList.length === 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", minHeight: "248px", color: "#94a3b8" }}>
                    <i className="fa fa-comments-o" style={{ fontSize: "32px", marginBottom: "8px", color: "var(--primary-color, #cbd5e1)" }} aria-hidden="true" />
                    <span style={{ fontSize: "13px", fontWeight: "600" }}>No conversation messages yet. Write a reply below to start the discussion.</span>
                  </div>
                ) : (
                  messageList.map((item) => (
                    <MessagingList
                      key={item.id}
                      message={item}
                      profilePicData={profilePicData}
                      profilePicTwoData={profilePicTwoData}
                    />
                  ))
                )}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: REPLY SECTION (40%) */}
          <div className="col-md-5 p-4 wf-reply-panel d-flex flex-column" style={{ height: "100%", overflowY: "auto" }}>
            <div className="wf-section-title">
              <i className="fa fa-reply mr-2 text-primary" aria-hidden="true"></i>
              Write a Reply
            </div>

            <div
              className={`drop-zone ${isDragging ? "dragging" : ""}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <i className="fa fa-cloud-upload" aria-hidden="true"></i>
              <p>Drag & drop files here</p>
              <span>or click to browse</span>
            </div>

            {documents.length > 0 && (
              <div className="file-preview-grid">
                {documents.map((file, index) => (
                  <div key={index} className="file-card">
                    <div className="file-icon-wrapper">
                      <i
                        className={`fa ${file.type?.includes("image")
                            ? "fa-file-image-o"
                            : file.type?.includes("pdf")
                              ? "fa-file-pdf-o"
                              : "fa-file-text-o"
                          }`}
                      ></i>
                    </div>
                    <div className="file-content">
                      <div className="file-title" title={file.name}>
                        {file.name}
                      </div>
                      <div className="file-meta">{formatFileSize(file.size)}</div>
                    </div>
                    <button
                      className="btn-remove-file"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFileRemove(file);
                      }}
                    >
                      <i className="fa fa-times"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="editor-wrapper flex-grow-1 mb-3">
              <SunEditor
                setOptions={{
                  height: "200px",
                  buttonList: toolbar,
                }}
                onChange={handleChange}
                setContents={content}
              />
            </div>

            <div className="wf-btn-group mt-auto pt-3 border-top">
              <button
                className="wf-btn wf-btn-secondary"
                onClick={() => handleMsgModalShowHide("")}
              >
                Close
              </button>
              <button className="wf-btn wf-btn-primary" onClick={onSubmit}>
                <i className="fa fa-paper-plane" aria-hidden="true"></i>
                Send Message
              </button>
            </div>
          </div>
        </div>

        <input
          type="file"
          ref={fileInputRef}
          onChange={onFileChange}
          multiple
          style={{ display: "none" }}
        />
      </Modal.Body>
    </Modal>
  );
};

export default MessagingPopup;
